import React from "react";
import BaseModel from "../models/BaseModel";
import type Consumer from "../models/Consumer";
import { ApplicationMode } from "../models/SettingsEnums";
import ConsumerDialog from "../modelDialogs/ConsumerDialog";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import BaseModelReference from "../models/BaseModelReference";
import Logger from "../Logger";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import type Room from "../models/Room";
import { channelTypeFromString, channelTypeInfo } from "../models/ChannelType";
import { isUiModeExpert } from "../uiMode";
import { itx } from "../translationItx/itx";
import ConnectableObject from "../models/ConnectableObject";

const logger = new Logger();

export default class ConsumerTreeViewModel extends TreeViewModel {
  get typedObj(): Consumer {
    return this.obj as Consumer;
  }

  // getPropertyControl(setPinned: (pinned: boolean) => void): any {
  //   return <ConsumerPropertyView obj={this.obj} setPinned={setPinned} />;
  // }

  getTitlePostfix_unused(): string | JSX.Element {
    var channel = this.typedObj.getConnectedChannel();
    if (channel?.mappedChannel) {
      channel = channel?.mappedChannel;
    }

    let sFuse = "";
    if (this.typedObj.fuse && state.settings.showCabinetPlaning) {
      sFuse = `  (${this.typedObj.fuse.fullTag})`;
    }
    if (channel) {
      return (
        <div>
          {/* <i className="las la-long-arrow-alt-right"></i> */}
          <i className="las la-rotate-90 la-tablet"></i> {channel.parent?.fullTag}:{channel.shortName} {sFuse}
        </div>
      );
    } else {
      const imgSrcError = "/images/alert.png";

      return (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <img
            src={imgSrcError}
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={itx(
              "consumerTreeViewModel.notConnectedWithActorChannel",
              "Nicht mit einem Aktorkanal verbunden",
            )}
            data-tooltip-place="right"
            data-tooltip-variant="error"
            style={{
              width: "25px",
              height: "25px",
              marginLeft: "10px",
              position: "relative",
            }}
          />
          {sFuse}
        </div>
      );
    }
  }
  getTitlePostfix(): string | JSX.Element {
    if (state.settings.applicationMode === ApplicationMode.ConceptOne) return "";
    ////////////////////////////////
    // show warning
    ////////////////////////////////
    let warningTexts = new Array<string>();
    const consumer = this.typedObj;
    ////////////////////////////////
    // check if connection with Actor or Supply
    /////////////////////////////
    const channel = consumer.getConnectedChannel();
    if (channel == undefined) {
      warningTexts.push(
        state.translate("consumerTreeViewModel.notConnectedWithActorChannel", "Nicht mit einem Aktorkanal verbunden"),
      );
    }
    ////////////////////////////////
    // check if to much via consumers
    /////////////////////////////
    const consumers = consumer.getAllViaConsumers();
    let toMannyConsumersForWires = false;
    consumers.forEach((viaConsumer) => {
      const neededNumberOfWires = consumer.wireRequirement?.numberOfLWiresWithoutPEandN || 1;
      const freeWiresWithOwn = consumer?.findFreeWireIndexs(neededNumberOfWires, viaConsumer);
      if (freeWiresWithOwn.length < neededNumberOfWires && !toMannyConsumersForWires) {
        toMannyConsumersForWires = true;
      }
    });
    if (toMannyConsumersForWires) {
      warningTexts.push(
        state.translate("consumerTreeViewModel.tooManyViaConsumers", "Zu viele Unterverteilungen für die Adernanzahl"),
      );
    }
    ///////////////////////////////
    // show warnings
    ///////////////////////////////
    if (warningTexts.length > 0) {
      const imgSrcWarning = "/images/warning.png";
      let imgArray = new Array<any>();
      warningTexts.forEach((text) => {
        imgArray.push(
          <img
            src={imgSrcWarning}
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={text}
            data-tooltip-place="bottom"
            data-tooltip-variant="warning" // no itx
            style={{
              width: "25px",
              height: "25px",
              marginLeft: "10px",
              position: "relative",
            }}
          />,
        );
      });

      return <div style={{ display: "flex", flexDirection: "row" }}>{imgArray}</div>;
    } else {
      return "";
    }
  }

  getIcon(context?: any): any | undefined {
    let url = "";

    if (this.typedObj.image != "") {
      url = this.typedObj.image;
    }

    return (
      <div style={{ display: "flex", flexDirection: "row" }}>
        <img draggable={false} className={`icon navIcon-${2}`} src={url} />
      </div>
    );
  }

  get isFolder(): boolean {
    return true;
    // return isUiModeExpert();
  }

  getTagNeeded(): boolean {
    return true;
  }

  get childCollection(): Array<BaseModel> | undefined {
    const res = new Array<BaseModelReference>();

    const channel = this.typedObj.getConnectedChannel();

    if (channel) {
      const ref = new BaseModelReference({ referencedObject: channel }, this.typedObj);
      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: true,
      };
      ref.overwriteFulltag = channel?.parent?.fullTag;
      ref.overwriteFullTitle = channel?.parent?.name;
      res.push(ref);
    }

    const terminal = this.typedObj.terminal;
    if (terminal) {
      const ref = new BaseModelReference({ referencedObject: terminal }, this.typedObj);

      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: false,
      };

      const url = state.hostBaseUrl + "images/terminal/terminal.png";
      ref.overwriteIcon = (
        <img className={`icon navIcon-4`} style={{ marginRight: "10px" }} src={url} draggable={false} />
      );

      ref.onDisconnect = () => {
        this.typedObj.connectWithTerminal(undefined);
        this.typedObj.raiseOnChange("childCollection");
        return true;
      };

      res.push(ref);
    }

    const viaConsumer = this.typedObj.getTerminalViaConsumer();
    if (viaConsumer) {
      const ref = new BaseModelReference({ referencedObject: viaConsumer }, this.typedObj);

      ref.overwriteFulltag = "";

      ref.overwriteFullTitle = (
        <div
          style={{
            marginLeft: "10px",
            backgroundColor: "yellow",
            borderRadius: "20px",
            paddingLeft: "10px",
            paddingRight: "10px",
          }}
        >
          {itx("consumerTreeViewModel.wiringVia", "Verkabelung über")} {viaConsumer.tag}:
          {this.typedObj.terminalViaWireName}
        </div>
      );

      ref.onDisconnect = () => {
        this.typedObj.setTerminalViaConsumer(undefined);
        this.typedObj.raiseOnChange("childCollection");
        return true;
      };
      res.push(ref);
    }

    const fuse = this.typedObj.fuse;
    if (fuse) {
      const ref = new BaseModelReference({ referencedObject: fuse }, this.typedObj);
      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: true,
      };
      const url = fuse.treeViewModel?.iconUrl;
      ref.overwriteIcon = (
        <img className={`icon navIcon-4`} style={{ marginRight: "10px" }} src={url} draggable={false} />
      );

      ref.onDisconnect = () => {
        this.typedObj.connectWithFuse(undefined);
        return true;
      };

      res.push(ref);
    }

    this.typedObj.getConnectedButtonChannels().forEach((buttonChannel) => {
      const ref = new BaseModelReference({ referencedObject: buttonChannel }, this.typedObj);
      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: true,
      };

      ref.overwriteFullTitle = buttonChannel.name;
      ref.overwritePostfix = "";

      res.push(ref);
    });

    this.typedObj.getConnectedScenes().forEach((scene) => {
      const ref = new BaseModelReference({ referencedObject: scene }, this.typedObj);
      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: true,
      };

      const url = scene.treeViewModel?.iconUrl;
      ref.overwriteIcon = (
        <img className={`icon navIcon-4`} style={{ marginRight: "10px" }} src={url} draggable={false} />
      );
      ref.overwriteFulltag = "";
      ref.overwriteFullTitle = scene.name;
      res.push(ref);
    });

    this.typedObj.getConnectedMultiplexers().forEach((multiplexer) => {
      const ref = new BaseModelReference({ referencedObject: multiplexer }, this.typedObj);
      ref.usefromRefencedTreeviewObject = {
        titlePostfix: false,
        fullTitle: false,
        icon: false,
      };

      const url = multiplexer.treeViewModel?.iconUrl;
      ref.overwriteIcon = (
        <img className={`icon navIcon-4`} style={{ marginRight: "10px" }} src={url} draggable={false} />
      );
      ref.overwriteFulltag = "";
      ref.overwriteFullTitle = multiplexer.name;

      res.push(ref);
    });

    if (isUiModeExpert()) {
      for (const signal of this.typedObj.signals.sortBy("tagOrName")) {
        for (const a of signal.groupAddresses) {
          if (a) {
            const ref = new BaseModelReference({ referencedObject: a }, this.typedObj);
            ref.usefromRefencedTreeviewObject.showChildern = isUiModeExpert();
            res.push(ref);
          }
        }
      }
    }

    return res;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////

  handleProperties(): void {
    state.dialogManager.openDialog(<ConsumerDialog obj={this.typedObj} />);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (copiedObjClassName == "Consumer" || copiedObjClassName == "Button") {
      return true;
    }
    return false;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if ((copiedObjClassName && copiedObjClassName == "Consumer") || copiedObjClassName == "Button") {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }
  getchannelTypeContextMenuItems() {
    const typeItems = new Array<any>();
    const changableChanneltype = this.typedObj.getChangableChannelType();

    for (const type of changableChanneltype) {
      const channelTypeInfoFromType = channelTypeInfo(channelTypeFromString(type));
      if (!channelTypeInfoFromType.canBeConsumer) {
        continue;
      }

      const url = channelTypeInfoFromType.image;
      let typeImage: any = null;

      if (url && url?.substring(0, 2) != "la") {
        typeImage = (
          <img className={`icon`} style={{ width: "25px", height: "25px", objectFit: "contain" }} src={url} />
        );
      }

      typeItems.push({
        id: "changeChanneltype" + type, // no itx
        iconName: type == this.typedObj.channelType ? "check" : "", // no itx
        iconColor: "green",
        label: (
          <div>
            {state.translate(`channelType.${type}`, type)} {typeImage}
          </div>
        ),
        onClick: (ev: any) => {
          this.typedObj.changeChannelType(type);
        },
      });
    }

    return {
      items: typeItems,
    };
  }

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    let items = new Array<any>();
    //// channeltype
    const channelTypeContextMenuItems = this.getchannelTypeContextMenuItems();
    if (channelTypeContextMenuItems) {
      let label = "ContextMenuLables.changeFunction"; // no itx
      if (channelTypeInfo(this.typedObj.channelType).isLight) {
        label = "ContextMenuLables.changeLight"; // no itx
      }
      items.push({
        // is a Submenu
        id: "changeChanneltype",
        iconName: "sync", // no itx
        label: label,
        menu: channelTypeContextMenuItems,
      });
    }

    try {
      subLabelPaste = state.contextMenuManager.clipboardData?.items?.[0]?.name;
    } catch (ex) {
      logger.error(ex);
    }

    items = items.concat([
      this.baseContextMenuItems_Copy(),
      // this.baseContextMenuItems_Cut(),
      this.baseContextMenuItems_Paste(subLabelPaste),
      this.baseContextMenuItems_Delete(),
      // this.baseContextMenuItems_Open(),
    ]);

    return {
      items: items,
    };
  }

  inlineButton_AutoConnect(referencedObj?: BaseModel) {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          const room = this.typedObj.parent as Room;
          const page = room?.page;
          if (page) {
            this.typedObj.autoConnectChannel(page);
          }
        }}
        iconName="link" // "expeditedssl",  // no itx
        tooltipKey="treeMenuIcon.connectWithActor" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "autoConnect"} // no itx
      />
    );
  }

  // getDiagnosticButtons(): JSX.Element[] | undefined {
  //   const signals = this.typedObj.signals;

  //   switch (this.typedObj.channelType) {
  //     case ChannelType.Dimmer:
  //       return [<TreeviewKnxDimButton signals={signals} />, <TreeviewKnxOnOffButton signals={signals} />];
  //     case ChannelType.Switch:
  //       return [<TreeviewKnxOnOffButton signals={signals} />];
  //   }
  // }

  getButtons() {
    const res = [super.inlineButton_Open("homePane.openConsumerSettings")]; // no itx

    const channel = this.typedObj.getConnectedChannel();
    if (channel) {
      return res;
    } else {
      return [...res, this.inlineButton_AutoConnect()];
    }
  }

  allowDrop(event: any): boolean {
    return true;
  }

  allowDrag(): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      return new DropResult(
        false,
        itx("Consumer.connectionOverTreeForbidden", "Verbindung kann nicht über den Baum hergestellt werden"),
      );
    }
    return await this.typedObj.handleDrop2(mode, obj);
  }
}
